import axios from 'axios';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

const api = axios.create({
    baseURL: API_BASE_URL,
});

// Helper to get cookie value
const getCookie = (name: string) => {
    if (typeof document === "undefined") return null;
    const nameLenPlus = (name.length + 1);
    return document.cookie
        .split(';')
        .map(c => c.trim())
        .filter(cookie => {
            return cookie.substring(0, nameLenPlus) === `${name}=`;
        })
        .map(cookie => {
            return decodeURIComponent(cookie.substring(nameLenPlus));
        })[0] || null;
};

// Add request interceptor to inject token
api.interceptors.request.use(
    (config) => {
        const token = getCookie("token");
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const setAuthToken = (token: string) => {
    // Legacy support or manual override if needed
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

// Add interceptor for better error logging
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (axios.isAxiosError(error)) {
            // Don't log 401 errors during logout 
            const isUnauthorized = error.response?.status === 401;
            const isLoggingOut = typeof window !== 'undefined' && window.location.pathname === '/login';

            if (!(isUnauthorized && isLoggingOut)) {
                console.error('Axios Error details:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data,
                    url: error.config?.url,
                });
            }
        }
        return Promise.reject(error);
    }
);

export interface Interview {
    id: string;
    candidate_id: string;
    job_id: string;
    audio_url: string;
    status: 'uploaded' | 'processing' | 'completed' | 'failed';
    created_at: string;
    candidate?: {
        id: string;
        full_name: string;
        email: string;
    };
    job?: {
        id: string;
        title: string;
        department: string;
    };
    transcript?: {
        text: string;
    };
    summary?: {
        experience: string;
        skills_technical: string;
        skills_soft: string;
        strengths: string;
        risks: string;
        motivation: string;
        recommendation: string;
        recommendation_note: string;
    };
}

export interface ScreenedCV {
    id: string;
    file_name: string;
    score: number | null;
    justification: string | null;
}

export interface CVScreening {
    id: string;
    job_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    created_at: string;
    screened_cvs: ScreenedCV[];
    job?: {
        id: string;
        title: string;
        department?: string;
        description: string | null;
    };
}

export const interviewsApi = {
    list: () => api.get<Interview[]>('/interviews/').then(res => res.data),
    upload: (formData: FormData, onProgress?: (percent: number) => void) =>
        api.post<{ id: string }>('/interviews/upload', formData, {
            onUploadProgress: (progressEvent) => {
                if (onProgress && progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            }
        }).then(res => res.data),
    process: (id: string) => api.post(`/interviews/${id}/process`).then(res => res.data),
    getSummary: (id: string) => api.get<Interview>(`/interviews/${id}/summary`).then(res => res.data),
    export: (id: string, format: string = 'markdown') =>
        api.get(`/interviews/${id}/export`, {
            params: { format },
            responseType: format === 'markdown' ? 'json' : 'blob'
        }).then(res => res.data),
    delete: (id: string) => api.delete(`/interviews/${id}`).then(res => res.data),
};

export const jobsApi = {
    list: () => api.get<any[]>('/jobs/').then(res => res.data),
    create: (data: { title: string; department?: string; description?: string; requirements?: string }) =>
        api.post<any>('/jobs/', data, {
            params: {
                description: data.description,
                requirements: data.requirements
            }
        }).then(res => res.data),
};

export const screeningApi = {
    list: () => api.get<CVScreening[]>('/screening/').then(res => res.data),
    create: (jobId: string) => api.post<CVScreening>('/screening/', { job_id: jobId }).then(res => res.data),
    uploadCvs: (id: string, files: File[]) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        return api.post(`/screening/${id}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => res.data);
    },
    getResults: (id: string) => api.get<CVScreening>(`/screening/${id}`).then(res => res.data),
    delete: (id: string) => api.delete<{ message: string }>(`/screening/${id}`).then(res => res.data),
    generateGuide: (screeningId: string, cvId: string) => api.post<any>(`/screening/${screeningId}/cv/${cvId}/generate-guide`).then(res => res.data),
    exportGuide: (screeningId: string, cvId: string, format: string) =>
        api.get(`/screening/${screeningId}/cv/${cvId}/export-guide`, {
            params: { format },
            responseType: 'blob'
        }).then(res => res.data),
};

export const subscriptionsApi = {
    createCheckout: (planId: string) =>
        api.post<{ checkout_url: string }>('/subscriptions/checkout', { plan_id: planId }).then(res => res.data),
    previewUpgrade: (planId: string) =>
        api.post<{ 
            amount_due: number; 
            next_renewal_amount?: number;
            subtotal?: number;
            total?: number;
            starting_balance?: number;
            currency: string; 
            next_payment_date: string; 
            is_new: boolean; 
            is_upgrade?: boolean;
            line_items?: Array<{
                amount: number;
                description: string;
                period_start: string;
                period_end: string;
                proration?: boolean;
            }>
        }>('/subscriptions/preview', { plan_id: planId }).then(res => res.data),
    getSubscription: () =>
        api.get<any>('/subscriptions/current').then(res => res.data),
    createPortal: () =>
        api.post<{ url: string }>('/subscriptions/portal').then(res => res.data),
    cancelSubscription: () =>
        api.post<{ message: string }>('/subscriptions/cancel').then(res => res.data),
    reactivateSubscription: () =>
        api.post<{ message: string }>('/subscriptions/reactivate').then(res => res.data),
    contact: (data: { name: string; email: string; subject: string; message: string; company_name?: string; phone?: string }) =>
        api.post<{ message: string }>('/subscriptions/contact', data).then(res => res.data),
};
